import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
	BanIcon,
	CheckCircleIcon,
	KeyIcon,
	MailIcon,
	MoreHorizontalIcon,
	ShieldCheckIcon,
	TrashIcon,
	UserPlusIcon,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { DataTable } from "@/components/data-table/data-table";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { isSuperAdminFlag } from "@/lib/super-admin";

export const Route = createFileRoute("/admin/users")({
	component: AdminUsers,
	ssr: false,
});

type UserPublicMetadata = {
	admin?: unknown;
	[key: string]: unknown;
};

type User = {
	id: string;
	emailAddresses: Array<{ emailAddress: string }>;
	firstName: string | null;
	lastName: string | null;
	createdAt: string;
	banned: boolean;
	publicMetadata: UserPublicMetadata;
	organizationMemberships?: Array<{
		organization: { id: string; name: string };
	}>;
};

function AdminUsers() {
	const queryClient = useQueryClient();
	const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
	const [selectedUser, setSelectedUser] = useState<User | null>(null);
	const [isMetadataDialogOpen, setIsMetadataDialogOpen] = useState(false);
	const [userToDelete, setUserToDelete] = useState<string | null>(null);

	const { data: users } = useSuspenseQuery({
		queryKey: ["admin", "users"],
		queryFn: async () => {
			const response = await fetch("/api/admin/users");
			if (!response.ok) {
				throw new Error("Failed to fetch users");
			}
			const data = await response.json();
			return data.users as User[];
		},
	});

	const inviteUserMutation = useMutation({
		mutationFn: async (data: {
			emailAddress: string;
			firstName: string;
			lastName: string;
		}) => {
			const response = await fetch("/api/admin/users", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(data),
			});
			if (!response.ok) {
				throw new Error("Failed to create user");
			}
			return response.json();
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
			setIsInviteDialogOpen(false);
			toast.success("User created successfully");
		},
		onError: (error: Error) => {
			toast.error(error.message || "Failed to create user");
		},
	});

	const updateUserMetadataMutation = useMutation({
		mutationFn: async ({
			id,
			publicMetadata,
		}: {
			id: string;
			publicMetadata: UserPublicMetadata;
		}) => {
			const response = await fetch(`/api/admin/users/${id}/metadata`, {
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ publicMetadata }),
			});
			if (!response.ok) {
				throw new Error("Failed to update user metadata");
			}
			return response.json();
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
			setIsMetadataDialogOpen(false);
			setSelectedUser(null);
			toast.success("User metadata updated successfully");
		},
		onError: () => {
			toast.error("Failed to update user metadata");
		},
	});

	const banUserMutation = useMutation({
		mutationFn: async ({ id, ban }: { id: string; ban: boolean }) => {
			const response = await fetch(`/api/admin/users/${id}/ban`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ ban }),
			});
			if (!response.ok) {
				throw new Error(`Failed to ${ban ? "ban" : "unban"} user`);
			}
			return response.json();
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
			toast.success(
				`User ${variables.ban ? "banned" : "unbanned"} successfully`,
			);
		},
		onError: (_, variables) => {
			toast.error(`Failed to ${variables.ban ? "ban" : "unban"} user`);
		},
	});

	const deleteUserMutation = useMutation({
		mutationFn: async (id: string) => {
			const response = await fetch(`/api/admin/users/${id}`, {
				method: "DELETE",
			});
			if (!response.ok) {
				throw new Error("Failed to delete user");
			}
			return response.json();
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
			toast.success("User deleted successfully");
			setUserToDelete(null);
		},
		onError: () => {
			toast.error("Failed to delete user");
		},
	});

	const columns: ColumnDef<User>[] = [
		{
			accessorKey: "emailAddresses",
			header: "Email",
			cell: ({ row }) => (
				<div className="flex items-center gap-2">
					<MailIcon className="h-4 w-4 text-muted-foreground" />
					<span>
						{row.original.emailAddresses[0]?.emailAddress || "No email"}
					</span>
				</div>
			),
		},
		{
			accessorKey: "name",
			header: "Name",
			cell: ({ row }) => {
				const name = [row.original.firstName, row.original.lastName]
					.filter(Boolean)
					.join(" ");
				return name || "No name";
			},
		},
		{
			accessorKey: "publicMetadata.admin",
			header: "Role",
			cell: ({ row }) => {
				const isAdmin = isSuperAdminFlag(row.original.publicMetadata?.admin);
				return isAdmin ? (
					<Badge className="gap-1">
						<ShieldCheckIcon className="h-3 w-3" />
						Admin
					</Badge>
				) : (
					<Badge variant="secondary">User</Badge>
				);
			},
		},
		{
			accessorKey: "banned",
			header: "Status",
			cell: ({ row }) =>
				row.original.banned ? (
					<Badge className="gap-1" variant="destructive">
						<BanIcon className="h-3 w-3" />
						Banned
					</Badge>
				) : (
					<Badge className="gap-1" variant="outline">
						<CheckCircleIcon className="h-3 w-3" />
						Active
					</Badge>
				),
		},
		{
			accessorKey: "organizationMemberships",
			header: "Organizations",
			cell: ({ row }) => {
				const orgs = row.original.organizationMemberships || [];
				return (
					<div className="flex flex-wrap gap-1">
						{orgs.length === 0 ? (
							<span className="text-muted-foreground text-sm">None</span>
						) : (
							orgs.slice(0, 2).map((membership) => (
								<Badge key={membership.organization.id} variant="secondary">
									{membership.organization.name}
								</Badge>
							))
						)}
						{orgs.length > 2 && (
							<Badge variant="secondary">+{orgs.length - 2}</Badge>
						)}
					</div>
				);
			},
		},
		{
			accessorKey: "createdAt",
			header: "Joined",
			cell: ({ getValue }) =>
				new Date(getValue() as string).toLocaleDateString(),
		},
		{
			id: "actions",
			cell: ({ row }) => (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button className="h-8 w-8 p-0" variant="ghost">
							<span className="sr-only">Open menu</span>
							<MoreHorizontalIcon className="h-4 w-4" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuLabel>Actions</DropdownMenuLabel>
						<DropdownMenuItem
							onClick={() => {
								setSelectedUser(row.original);
								setIsMetadataDialogOpen(true);
							}}
						>
							<KeyIcon className="mr-2 h-4 w-4" />
							Edit Metadata
						</DropdownMenuItem>
						<DropdownMenuItem
							onClick={() =>
								banUserMutation.mutate({
									id: row.original.id,
									ban: !row.original.banned,
								})
							}
						>
							<BanIcon className="mr-2 h-4 w-4" />
							{row.original.banned ? "Unban" : "Ban"} User
						</DropdownMenuItem>
						<DropdownMenuSeparator />
						<DropdownMenuItem
							className="text-destructive"
							onClick={() => setUserToDelete(row.original.id)}
						>
							<TrashIcon className="mr-2 h-4 w-4" />
							Delete
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			),
		},
	];

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="font-bold text-3xl">Users</h1>
					<p className="text-muted-foreground">
						Manage all users in the system
					</p>
				</div>
				<Dialog onOpenChange={setIsInviteDialogOpen} open={isInviteDialogOpen}>
					<DialogTrigger asChild>
						<Button>
							<UserPlusIcon className="mr-2 h-4 w-4" />
							Create User
						</Button>
					</DialogTrigger>
					<DialogContent>
						<form
							onSubmit={(e) => {
								e.preventDefault();
								const formData = new FormData(e.currentTarget);
								inviteUserMutation.mutate({
									emailAddress: formData.get("emailAddress") as string,
									firstName: formData.get("firstName") as string,
									lastName: formData.get("lastName") as string,
								});
							}}
						>
							<DialogHeader>
								<DialogTitle>Create User</DialogTitle>
								<DialogDescription>
									Create a new user account. They will sign in using
									passwordless authentication.
								</DialogDescription>
							</DialogHeader>
							<div className="grid gap-4 py-4">
								<div className="grid gap-2">
									<Label htmlFor="emailAddress">Email Address</Label>
									<Input
										id="emailAddress"
										name="emailAddress"
										placeholder="user@example.com"
										required
										type="email"
									/>
								</div>
								<div className="grid gap-2">
									<Label htmlFor="firstName">First Name</Label>
									<Input
										id="firstName"
										name="firstName"
										placeholder="John"
										type="text"
									/>
								</div>
								<div className="grid gap-2">
									<Label htmlFor="lastName">Last Name</Label>
									<Input
										id="lastName"
										name="lastName"
										placeholder="Doe"
										type="text"
									/>
								</div>
							</div>
							<DialogFooter>
								<Button
									onClick={() => setIsInviteDialogOpen(false)}
									type="button"
									variant="outline"
								>
									Cancel
								</Button>
								<Button disabled={inviteUserMutation.isPending} type="submit">
									{inviteUserMutation.isPending ? "Creating..." : "Create User"}
								</Button>
							</DialogFooter>
						</form>
					</DialogContent>
				</Dialog>
			</div>

			<DataTable columns={columns} data={users} />

			<AlertDialog
				onOpenChange={(open) => {
					if (!open) {
						setUserToDelete(null);
					}
				}}
				open={Boolean(userToDelete)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete user</AlertDialogTitle>
						<AlertDialogDescription>
							This action will permanently delete the user account.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => setUserToDelete(null)}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (userToDelete) {
									deleteUserMutation.mutate(userToDelete);
								}
								setUserToDelete(null);
							}}
						>
							Delete
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{selectedUser && (
				<Dialog
					onOpenChange={setIsMetadataDialogOpen}
					open={isMetadataDialogOpen}
				>
					<DialogContent>
						<form
							onSubmit={(e) => {
								e.preventDefault();
								const formData = new FormData(e.currentTarget);
								updateUserMetadataMutation.mutate({
									id: selectedUser.id,
									publicMetadata: {
										...selectedUser.publicMetadata,
										admin: formData.get("admin") === "on",
									},
								});
							}}
						>
							<DialogHeader>
								<DialogTitle>Edit User Metadata</DialogTitle>
								<DialogDescription>
									Update user permissions and metadata
								</DialogDescription>
							</DialogHeader>
							<div className="grid gap-4 py-4">
								<div className="flex items-center space-x-2">
									<Checkbox
										defaultChecked={isSuperAdminFlag(
											selectedUser.publicMetadata?.admin,
										)}
										id="admin"
										name="admin"
									/>
									<Label className="cursor-pointer" htmlFor="admin">
										Grant admin privileges
									</Label>
								</div>
							</div>
							<DialogFooter>
								<Button
									onClick={() => {
										setIsMetadataDialogOpen(false);
										setSelectedUser(null);
									}}
									type="button"
									variant="outline"
								>
									Cancel
								</Button>
								<Button
									disabled={updateUserMetadataMutation.isPending}
									type="submit"
								>
									{updateUserMetadataMutation.isPending
										? "Updating..."
										: "Update"}
								</Button>
							</DialogFooter>
						</form>
					</DialogContent>
				</Dialog>
			)}
		</div>
	);
}
